import { EnqueueItem } from '../Hooks/usePendingScoreQueue';
import { resolveHoleScoreArrayIndex } from './resolveHoleScoreArrayIndex';
import { MutableRefObject } from 'react';

export interface BuildHoleEnqueueItemsParams {
    scoreData: any[];
    scoreDataRef: MutableRefObject<any>;
    holeNumber: number;
    currentRound: number;
    scoreCode: string;
    eventData: any;
    roundTotalHoles: number;
    /** When true, enqueue every player's hole score even if unchanged vs scoreDataRef. */
    force?: boolean;
}

export function buildHoleEnqueueItems({
    scoreData,
    scoreDataRef,
    holeNumber,
    currentRound,
    scoreCode,
    eventData,
    roundTotalHoles,
    force = false,
}: BuildHoleEnqueueItemsParams): EnqueueItem[] {
    const isMarker = scoreData.every((score: any) =>
        score.roundScores[currentRound - 1].markerScoreCode === scoreCode);

    if (!isMarker) return [];

    const ref = scoreDataRef.current;
    const itemsToEnqueue: EnqueueItem[] = [];

    const teeTimeForPace: string | undefined =
        scoreData?.[0]?.roundScores?.[currentRound - 1]?.teeTime;
    const isOpponent = eventData?.scoreType?.toLowerCase() === 'opponent';
    const paceContext = (eventData as any)?._id && teeTimeForPace
        ? {
            eventID: (eventData as any)._id as string,
            roundNumber: currentRound,
            teeTime: teeTimeForPace,
            scoreCode: isOpponent ? scoreCode : null,
            totalHoles: roundTotalHoles,
          }
        : undefined;

    scoreData.forEach((item: any, index: number) => {
        const holeScores = item.roundScores[currentRound - 1].holeScores;
        const holeIdx = resolveHoleScoreArrayIndex(holeScores, holeNumber);
        const currentHoleScore = holeScores[holeIdx];
        const refRow = Array.isArray(ref) ? ref[index]?.roundScores?.[currentRound - 1]?.holeScores : undefined;
        const holeScoreRef = Array.isArray(refRow) ? refRow[holeIdx] : undefined;

        if (force || JSON.stringify(holeScoreRef) !== JSON.stringify(currentHoleScore)) {
            itemsToEnqueue.push({
                eventScoreID: item._id,
                roundIndex: currentRound - 1,
                holeIndex: holeIdx,
                holeScore: currentHoleScore,
                ...(paceContext ? { paceContext } : {}),
            });
        }
    });

    return itemsToEnqueue;
}

export function isScoreCheckComplete(scoreCheckData: any[], eventData: any, scoreCode: string): boolean {
    if (!scoreCheckData?.length) return true;

    if (eventData?.scoreType?.toLowerCase() === 'opponent') {
        const thisPlayerIncomplete = scoreCheckData.filter((score: any) =>
            score.incompletePlayerHoles.length > 0 && score.markerScoreCode === scoreCode);
        const thisMarkerIncomplete = scoreCheckData.filter((score: any) =>
            score.incompleteMarkerHoles.length > 0 && score.markerScoreCode === scoreCode);
        const otherPlayerIncomplete = scoreCheckData.filter((score: any) =>
            score.incompletePlayerHoles.length > 0 && score.scoreCode === scoreCode);
        const otherMarkerIncomplete = scoreCheckData.filter((score: any) =>
            score.incompleteMarkerHoles.length > 0 && score.scoreCode === scoreCode);

        return thisPlayerIncomplete.length === 0
            && thisMarkerIncomplete.length === 0
            && otherPlayerIncomplete.length === 0
            && otherMarkerIncomplete.length === 0;
    }

    return scoreCheckData.filter((score: any) => score.incompletePlayerHoles.length > 0).length === 0;
}
